import React, { useState, useEffect, useRef, useCallback } from 'react'
import { PhoneOff } from 'lucide-react'
import RippleBackground from './RippleBackground'
import { Role } from '../types'
import { useTranscription } from '../hooks/useTranscription'
import { useTimer } from '../hooks/useTimer'

interface CallInterfaceProps {
  role: Role
  sessionId: string
  candidateName: string
  onCallStart: () => void
  onCallEnd: () => void
  backendWsRef: React.RefObject<WebSocket | null>
}

const CallInterface: React.FC<CallInterfaceProps> = ({
  role,
  sessionId,
  onCallEnd,
  backendWsRef
}) => {
  console.log('CallInterface: Component mounted/remounted')
  const [isSpeaking, setIsSpeaking] = useState(false)
  const transcriptionStartedRef = useRef(false)
  
  const { duration, startTimer, stopTimer } = useTimer(120) // Start with 2:00 (120 seconds)
  
  const audioRef = useRef<HTMLAudioElement>(null)
  const audioChunksRef = useRef<ArrayBuffer[]>([])
  const audioContextRef = useRef<AudioContext | null>(null)
  const isPlayingRef = useRef<boolean>(false)
  const isUnmountingRef = useRef<boolean>(false)
  const isTimerEndFlowRef = useRef<boolean>(false)
  const activeAudioSourcesRef = useRef<AudioBufferSourceNode[]>([])
  const messageHandlerSetupRef = useRef<boolean>(false)
  
  // Memoize callbacks to prevent re-creation
  const handleFinalTranscript = useCallback((text: string) => {
    // Send final transcript to backend
    if (backendWsRef.current && backendWsRef.current.readyState === WebSocket.OPEN) {
      backendWsRef.current.send(JSON.stringify({
        type: 'transcript',
        sessionId: sessionId,
        transcript: text,
        timestamp: Date.now()
      }))
    }
  }, [sessionId, backendWsRef])

  const handleTranscriptionError = useCallback((error: string) => {
    console.error('Transcription error:', error)
  }, [])
  
  // Transcription hook
  const {
    isConnected: isTranscriptionConnected,
    startTranscription,
    stopTranscription,
    disconnect: disconnectTranscription
  } = useTranscription({
    transcriptionServerUrl: import.meta.env.VITE_TRANSCRIPTION_WS_URL || 'wss://talkwithzayn.getgeek.ai/transcription-ai/ws/transcribe-english',
    onFinalTranscript: handleFinalTranscript,
    onError: handleTranscriptionError
  })

  // Check for pending greeting audio and play it first
  useEffect(() => {
    // Set a global flag to prevent multiple plays
    if ((window as any).hasPlayedGreetingAudio) {
      console.log('CallInterface: Greeting audio already played, skipping')
      return
    }
    
    // Wait a bit longer to ensure all audio chunks have been buffered
    const checkForPendingAudio = () => {
      console.log('CallInterface: Checking for pending audio...')
      const pendingAudio = (window as any).pendingGreetingAudio
      console.log('CallInterface: pendingGreetingAudio exists:', !!pendingAudio)
      console.log('CallInterface: pendingGreetingAudio length:', pendingAudio ? pendingAudio.length : 'N/A')
      
      if (pendingAudio && pendingAudio.length > 0) {
        // Set flag immediately to prevent replay
        ;(window as any).hasPlayedGreetingAudio = true
        
        console.log('CallInterface: Found pending greeting audio, playing it first:', pendingAudio.length, 'chunks')
        
        // Clear the pending audio immediately to prevent replay
        ;(window as any).pendingGreetingAudio = []
        
        // Play the pending audio chunks
        const playPendingAudio = async () => {
          console.log('CallInterface: Starting to play pending audio chunks...')
          for (let i = 0; i < pendingAudio.length; i++) {
            const audioChunk = pendingAudio[i]
            console.log(`CallInterface: Playing audio chunk ${i + 1}/${pendingAudio.length}, size:`, audioChunk.byteLength)
            await handleAudioChunk(audioChunk)
          }
          console.log('CallInterface: Finished playing pending greeting audio')
        }
        
        playPendingAudio()
      } else {
        console.log('CallInterface: No pending audio found or empty array')
      }
    }
    
    // Check immediately and after a short delay to catch any late chunks
    checkForPendingAudio()
    setTimeout(checkForPendingAudio, 500)
  }, [])

  // Setup WebSocket message handlers for the existing connection
  useEffect(() => {
    // Check if we have an existing WebSocket connection from DialingScreen
    if (!backendWsRef.current) {
      console.log('CallInterface: No existing WebSocket connection from DialingScreen')
      return
    }
    
    // Prevent setting up message handlers multiple times
    if (messageHandlerSetupRef.current) {
      console.log('CallInterface: Message handlers already set up, skipping...')
      return
    }
    
    messageHandlerSetupRef.current = true
    console.log('CallInterface: Using existing WebSocket connection from DialingScreen')
    
    const ws = backendWsRef.current
    
    // Check if connection is already open
    if (ws.readyState === WebSocket.OPEN) {
      console.log('CallInterface: WebSocket is already open')
      // Start timer automatically
      startTimer()
      
      // Send a message to indicate we're ready for conversation
      ws.send(JSON.stringify({
        type: 'ready_for_conversation',
        sessionId: sessionId,
        role: role.id
      }))
    }
    
    // Store original handlers to avoid overwriting
    const originalOnOpen = ws.onopen
    const originalOnClose = ws.onclose
    const originalOnError = ws.onerror
    
    // Setup new message handler that also calls original if it exists
    ws.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        // Handle binary audio data
        console.log('CallInterface: Received audio chunk, size:', event.data.byteLength)
        handleAudioChunk(event.data)
      } else {
        // Handle JSON messages
        try {
          const message = JSON.parse(event.data)
          console.log('CallInterface: Received WebSocket message:', message.type)
          handleWebSocketMessage(message)
        } catch (error) {
          console.error('CallInterface: Error parsing message:', error)
        }
      }
    }
    
    // Setup onopen handler (in case connection opens after this component mounts)
    ws.onopen = (event) => {
      console.log('CallInterface: WebSocket opened')
      // Start timer automatically
      startTimer()
      
      // Send a message to indicate we're ready for conversation
      ws.send(JSON.stringify({
        type: 'ready_for_conversation',
        sessionId: sessionId,
        role: role.id
      }))
      
      if (originalOnOpen) originalOnOpen.call(ws, event)
    }
    
    ws.onclose = (event) => {
      console.log('CallInterface: WebSocket closed')
      if (originalOnClose) originalOnClose.call(ws, event)
    }
    
    ws.onerror = (event) => {
      console.error('CallInterface: WebSocket error:', event)
      if (originalOnError) originalOnError.call(ws, event)
    }
    
    return () => {
      console.log('CallInterface: useEffect cleanup running')
      isUnmountingRef.current = true
      // DON'T close the WebSocket - it's managed by App.tsx
      // Just cleanup audio context
      if (audioContextRef.current) {
        audioContextRef.current.close()
        audioContextRef.current = null
      }
      // Clear audio queue
      audioChunksRef.current = []
      isPlayingRef.current = false
    }
  }, [backendWsRef, sessionId, role.id, startTimer]) // Add dependencies

  // Monitor transcription connection state changes and start transcription when ready
  useEffect(() => {
    console.log('CallInterface: Transcription connection state changed:', isTranscriptionConnected)
    if (isTranscriptionConnected && !transcriptionStartedRef.current) {
      console.log('Transcription connected! Starting transcription...')
      transcriptionStartedRef.current = true
      // Add a small delay to ensure the connection is fully established
      setTimeout(() => {
        try {
          startTranscription()
        } catch (error) {
          console.warn('Transcription not available:', error)
          transcriptionStartedRef.current = false // Reset flag on error
        }
      }, 100)
    }
  }, [isTranscriptionConnected, startTranscription])

  // Monitor timer and send timer_end message when it reaches 0
  useEffect(() => {
    if (duration <= 0 && !isTimerEndFlowRef.current) {
      console.log('Timer reached 0 - sending timer_end message')
      console.log('Current timer end flow flag:', isTimerEndFlowRef.current)
      // DON'T set isTimerEndFlowRef.current = true here!
      // It should only be set when we receive timer_end_confirmed
      // Send timer_end message to backend
      if (backendWsRef.current && backendWsRef.current.readyState === WebSocket.OPEN) {
        backendWsRef.current.send(JSON.stringify({
          type: 'timer_end',
          sessionId: sessionId,
          timestamp: Date.now()
        }))
        console.log('Timer end message sent to backend')
      } else {
        console.log('WebSocket not ready, cannot send timer_end message')
      }
    }
  }, [duration, sessionId, backendWsRef])

  const handleWebSocketMessage = (message: any) => {
    switch (message.type) {
      case 'interview_started':
        // Interview has started, we're already in call screen
        console.log('Interview started:', message.greeting)
        break
      case 'response':
        // Audio will be streamed separately
        break
      case 'timer_end_confirmed':
        // Timer end message was sent successfully, wait for audio to finish
        console.log('Timer end confirmed:', message.message)
        // NOW set the flag to wait for audio completion
        isTimerEndFlowRef.current = true
        console.log('Timer end flow flag set to true - waiting for audio to complete')
        break
      case 'interview_end':
        // Backend wants to end the interview
        console.log('Interview end flag received from backend')
        handleEndCall()
        break
      case 'error':
        console.error('Server error:', message.error)
        break
    }
  }

  const handleAudioChunk = async (audioData: ArrayBuffer) => {
    // Add to audio queue
    audioChunksRef.current.push(audioData)
    
    // Start playing if not already playing
    if (!isPlayingRef.current) {
      await playAudioQueue()
    }
  }

  const playAudioQueue = async () => {
    if (audioChunksRef.current.length === 0) {
      isPlayingRef.current = false
      setIsSpeaking(false)
      return
    }

    isPlayingRef.current = true
    setIsSpeaking(true)

    try {
      // Initialize audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)()
      }

      // Resume context if suspended
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume()
      }

      // Process all chunks in queue
      while (audioChunksRef.current.length > 0) {
        // Check if queue was cleared during processing
        if (audioChunksRef.current.length === 0) {
          console.log('Audio queue was cleared during processing, stopping playback')
          break
        }
        
        const chunk = audioChunksRef.current.shift()
        if (!chunk) continue

        // Create WAV chunk from PCM data
        const wavChunk = createWavChunk(chunk)
        
        // Decode the WAV audio
        const audioBuffer = await audioContextRef.current.decodeAudioData(wavChunk)
        
        // Create audio source
        const source = audioContextRef.current.createBufferSource()
        source.buffer = audioBuffer
        source.connect(audioContextRef.current.destination)
        
        // Track active audio sources
        activeAudioSourcesRef.current.push(source)
        
        // Play the chunk
        source.start()
        
        // Wait for this chunk to finish before playing the next
        await new Promise<void>(resolve => {
          source.onended = () => {
            // Remove from active sources when finished
            activeAudioSourcesRef.current = activeAudioSourcesRef.current.filter(s => s !== source)
            resolve()
          }
        })
      }

      // All chunks processed
      isPlayingRef.current = false
      setIsSpeaking(false)
      
      // Check if this was the timer end flow
      if (isTimerEndFlowRef.current) {
        console.log('Timer end audio finished, ending call')
        console.log('Audio chunks remaining:', audioChunksRef.current.length)
        handleEndCall()
      }
    } catch (error) {
      console.error('Error playing audio:', error)
      // Fallback: play as audio element
      await playAudioChunksAsBlob()
    }

    // Note: isPlayingRef and setIsSpeaking are already set above
    // No need to set them again or call handleEndCall again
  }

  const createWavChunk = (pcmData: ArrayBuffer): ArrayBuffer => {
    // Create WAV header for PCM data
    const sampleRate = 24000
    const numChannels = 1
    const bitsPerSample = 16
    const byteRate = sampleRate * numChannels * bitsPerSample / 8
    const blockAlign = numChannels * bitsPerSample / 8
    const dataSize = pcmData.byteLength
    const fileSize = 36 + dataSize
    
    const buffer = new ArrayBuffer(44 + dataSize)
    const view = new DataView(buffer)
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }
    
    writeString(0, 'RIFF')
    view.setUint32(4, fileSize, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, numChannels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, byteRate, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitsPerSample, true)
    writeString(36, 'data')
    view.setUint32(40, dataSize, true)
    
    // Copy PCM data
    const pcmView = new Uint8Array(pcmData)
    const wavView = new Uint8Array(buffer, 44)
    wavView.set(pcmView)
    
    return buffer
  }

  const playAudioChunksAsBlob = async () => {
    // Fallback method: combine chunks and play as audio element
    if (audioChunksRef.current.length === 0) return

    const chunks = audioChunksRef.current.splice(0)
    // Create WAV file from PCM chunks
    const wavBlob = createWavBlob(chunks)
    const url = URL.createObjectURL(wavBlob)
    
    if (audioRef.current) {
      audioRef.current.src = url
      audioRef.current.play().catch(error => {
        console.error('Error playing audio blob:', error)
      })
      
      audioRef.current.onended = () => {
        URL.revokeObjectURL(url)
        if (audioChunksRef.current.length > 0) {
          playAudioChunksAsBlob()
        } else {
          // If we're in timer end flow and no more audio, automatically end the call
          if (isTimerEndFlowRef.current) {
            console.log('Timer end audio finished (fallback) - automatically ending call')
            handleEndCall()
          }
        }
      }
    }
  }

  const createWavBlob = (pcmChunks: ArrayBuffer[]): Blob => {
    // Combine all PCM chunks
    const totalSize = pcmChunks.reduce((sum, chunk) => sum + chunk.byteLength, 0)
    const combinedPcm = new Uint8Array(totalSize)
    let offset = 0
    
    for (const chunk of pcmChunks) {
      combinedPcm.set(new Uint8Array(chunk), offset)
      offset += chunk.byteLength
    }
    
    // Create WAV file
    const sampleRate = 24000
    const numChannels = 1
    const bitsPerSample = 16
    const byteRate = sampleRate * numChannels * bitsPerSample / 8
    const blockAlign = numChannels * bitsPerSample / 8
    const dataSize = combinedPcm.byteLength
    const fileSize = 36 + dataSize
    
    const buffer = new ArrayBuffer(44 + dataSize)
    const view = new DataView(buffer)
    
    // WAV header
    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i))
      }
    }
    
    writeString(0, 'RIFF')
    view.setUint32(4, fileSize, true)
    writeString(8, 'WAVE')
    writeString(12, 'fmt ')
    view.setUint32(16, 16, true)
    view.setUint16(20, 1, true)
    view.setUint16(22, numChannels, true)
    view.setUint32(24, sampleRate, true)
    view.setUint32(28, byteRate, true)
    view.setUint16(32, blockAlign, true)
    view.setUint16(34, bitsPerSample, true)
    writeString(36, 'data')
    view.setUint32(40, dataSize, true)
    
    // Copy PCM data
    const wavView = new Uint8Array(buffer, 44)
    wavView.set(combinedPcm)
    
    return new Blob([buffer], { type: 'audio/wav' })
  }



  // Global function to stop all audio immediately
  const stopAllAudioImmediately = async () => {
    console.log('Stopping all audio immediately')
    console.log('Active audio sources count:', activeAudioSourcesRef.current.length)
    console.log('Audio context state:', audioContextRef.current?.state)
    console.log('Is playing:', isPlayingRef.current)
    console.log('Is speaking:', isSpeaking)
    
    // Stop all active audio sources
    activeAudioSourcesRef.current.forEach((source, index) => {
      try {
        console.log(`Stopping audio source ${index}:`, source)
        source.stop()
        console.log(`Force stopped audio source ${index}`)
      } catch (error) {
        console.log(`Error force stopping audio source ${index}:`, error)
      }
    })
    activeAudioSourcesRef.current = []
    
    // Close audio context (only when call is ending)
    if (audioContextRef.current) {
      try {
        console.log('Closing audio context, current state:', audioContextRef.current.state)
        audioContextRef.current.close()
        console.log('Closed audio context')
      } catch (error) {
        console.log('Error closing audio context:', error)
      }
      audioContextRef.current = null
    }
    
    // Note: Removed temp audio context creation to avoid interference with normal playback
    
    // Stop all HTML audio elements more aggressively
    const audioElements = document.querySelectorAll('audio')
    console.log('Found HTML audio elements:', audioElements.length)
    audioElements.forEach((audio, index) => {
      try {
        console.log(`Stopping HTML audio element ${index}:`, audio.src, 'paused:', audio.paused)
        audio.pause()
        audio.currentTime = 0
        audio.src = ''
        audio.load() // Force reload to clear any buffered audio
        console.log(`Force stopped HTML audio element ${index}`)
      } catch (error) {
        console.log(`Error force stopping HTML audio element ${index}:`, error)
      }
    })
    
    // Clear audio queue
    console.log('Clearing audio queue, current length:', audioChunksRef.current.length)
    audioChunksRef.current = []
    isPlayingRef.current = false
    setIsSpeaking(false)
    
    // Force stop any remaining audio by creating and destroying audio elements
    try {
      const tempAudio = new Audio()
      tempAudio.pause()
      tempAudio.src = ''
      console.log('Created and stopped temp audio element')
    } catch (error) {
      console.log('Error with temp audio element:', error)
    }
    
    // Use Media Session API to stop all audio if available
    try {
      if ('mediaSession' in navigator) {
        navigator.mediaSession.playbackState = 'none'
        console.log('Set media session playback state to none')
      }
    } catch (error) {
      console.log('Error with media session API:', error)
    }
    
    // Force stop by dispatching a custom event that all audio elements can listen to
    try {
      const stopEvent = new CustomEvent('forceStopAudio')
      window.dispatchEvent(stopEvent)
      console.log('Dispatched forceStopAudio event')
    } catch (error) {
      console.log('Error dispatching forceStopAudio event:', error)
    }
    
    // NUCLEAR OPTION: Stop ALL audio by manipulating the DOM
    try {
      // Find ALL audio and video elements on the page
      const allMediaElements = document.querySelectorAll('audio, video')
      console.log('Found all media elements:', allMediaElements.length)
      
      allMediaElements.forEach((element, index) => {
        try {
          const mediaElement = element as HTMLMediaElement
          console.log(`Stopping media element ${index}:`, element.tagName, mediaElement.src)
          mediaElement.pause()
          mediaElement.currentTime = 0
          mediaElement.src = ''
          mediaElement.load()
          console.log(`Force stopped media element ${index}`)
        } catch (error) {
          console.log(`Error stopping media element ${index}:`, error)
        }
      })
      
      // Note: Removed temp audio context manipulation to avoid interference with normal playback
      
    } catch (error) {
      console.log('Error with nuclear audio stop:', error)
    }
  }

  // Make the stop function globally accessible
  ;(window as any).stopAllAudioImmediately = stopAllAudioImmediately

  const handleEndCall = async () => {
    console.log('CallInterface: Ending call - cleaning up resources')
    
    // Set global flag immediately - this is the most important step
    ;(window as any).callEnded = true
    
    // Stop DialingScreen audio immediately if it's still playing
    if ((window as any).stopDialingAudio) {
      console.log('CallInterface: Calling stopDialingAudio to stop start message audio')
      ;(window as any).stopDialingAudio()
    }
    
    // Clear audio queue IMMEDIATELY - this will stop audio playback
    audioChunksRef.current = []
    
    // Stop all audio immediately
    await stopAllAudioImmediately()
    
    // Dispatch custom event to notify DialingScreen
    window.dispatchEvent(new CustomEvent('callEnded'))
    
    // IMMEDIATELY stop all audio processing
    isPlayingRef.current = false
    setIsSpeaking(false)
    
    // Stop all active audio sources immediately
    console.log('CallInterface: Stopping active audio sources:', activeAudioSourcesRef.current.length)
    activeAudioSourcesRef.current.forEach(source => {
      try {
        source.stop()
        console.log('Stopped audio source')
      } catch (error) {
        // Source might already be stopped, ignore error
        console.log('Audio source already stopped or error stopping:', error)
      }
    })
    activeAudioSourcesRef.current = []
    
    // Force stop audio context and create new one to ensure clean state
    if (audioContextRef.current) {
      console.log('CallInterface: Force closing audio context')
      try {
        audioContextRef.current.close()
      } catch (error) {
        console.log('Error closing audio context:', error)
      }
      audioContextRef.current = null
    }
    
    // Stop ALL audio elements on the page (more aggressive)
    const audioElements = document.querySelectorAll('audio')
    audioElements.forEach(audio => {
      try {
        audio.pause()
        audio.currentTime = 0
        audio.src = ''
        console.log('Stopped HTML audio element')
      } catch (error) {
        console.log('Error stopping audio element:', error)
      }
    })
    
    // Force stop any Web Audio API contexts globally
    if (typeof window !== 'undefined') {
      try {
        // Try to stop any suspended contexts
        if (window.AudioContext || (window as any).webkitAudioContext) {
          const AudioContext = window.AudioContext || (window as any).webkitAudioContext
          // This is a bit of a hack, but we'll try to create and immediately close a context
          // to ensure any suspended contexts are properly handled
          const tempContext = new AudioContext()
          if (tempContext.state === 'suspended') {
            tempContext.close()
          }
        }
      } catch (error) {
        console.log('Error with global audio context cleanup:', error)
      }
    }
    
    // Stop and disconnect transcription
    try {
      stopTranscription()
      disconnectTranscription()
    } catch (error) {
      console.warn('Error stopping/disconnecting transcription:', error)
    }
    
    // Send end call message to backend
    if (backendWsRef.current && backendWsRef.current.readyState === WebSocket.OPEN) {
      backendWsRef.current.send(JSON.stringify({
        type: 'end_call',
        sessionId: sessionId
      }))
    }
    
    // DON'T close the WebSocket here - it's managed by App.tsx
    // App.tsx will close it when transitioning to the end screen
    
    // Stop timer
    stopTimer()
    
    // Call parent handler
    onCallEnd()
  }

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <div className="min-h-screen w-screen flex flex-col items-center justify-between relative overflow-hidden p-4 sm:p-8 md:p-16">
      {/* Background Video */}
      <video 
        autoPlay 
        loop 
        muted 
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src="https://pub-b4a0d7160e804448b1c1062c499df6e5.r2.dev/background.mp4" type="video/mp4" />
      </video>
      
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/20"></div>
      
      {/* Background concentric circles - shared */}
      <RippleBackground anchorToParentCenter />

      {/* Top section - Role title and Timer */}
      <div className="relative z-10 flex flex-col items-center space-y-4 sm:space-y-6 md:space-y-8">
        {/* Role title */}
        <div>
          <h1 className="text-xl sm:text-2xl md:text-3xl font-semibold text-white text-center">{role.title}</h1>
        </div>
        
        {/* Timer */}
        <div>
          <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-white">{formatTime(duration)}</p>
        </div>
      </div>

      {/* Central logo - positioned slightly above exact center for visual alignment */}
      <div className="relative z-10 flex items-center justify-center -mt-4 sm:-mt-5 md:-mt-6">
        <div className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 bg-white rounded-full flex items-center justify-center shadow-2xl">
          <img 
            src="https://public-getgeek-s3-bucket-storage.s3.amazonaws.com/qrs/zayn/center_call_interface.png" 
            alt="Zayn Logo" 
            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-contain"
          />
        </div>
      </div>
      
      {/* Bottom section - End call button */}
      <div className="relative z-10">
        {/* End call button */}
            <button
          onMouseDown={async () => {
            // Stop audio immediately on mouse down (before click)
            console.log('End call button pressed - stopping audio immediately')
            ;(window as any).callEnded = true
            if ((window as any).stopAllAudioImmediately) {
              await (window as any).stopAllAudioImmediately()
            }
          }}
              onClick={handleEndCall}
          className="w-10 h-10 sm:w-12 sm:h-12 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110"
            >
          <PhoneOff className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </button>
      </div>
      
      {/* Hidden audio element for playing responses */}
      <audio 
        ref={audioRef} 
        className="hidden"
        onLoadStart={(e) => {
          // Listen for force stop events
          const handleForceStop = () => {
            console.log('Audio element received force stop event')
            if (e.currentTarget) {
              e.currentTarget.pause()
              e.currentTarget.currentTime = 0
              e.currentTarget.src = ''
            }
          }
          window.addEventListener('forceStopAudio', handleForceStop)
          
          // Cleanup listener when component unmounts
          return () => {
            window.removeEventListener('forceStopAudio', handleForceStop)
          }
        }}
      />
    </div>
  )
}

export default CallInterface
